/**
 * React Provider for Production GraphQL Client
 */

import { ApolloProvider } from '@apollo/client/react';
import React, { createContext, useContext, useEffect, useState } from 'react';

import { createFerpClient } from './client';
import { FerpApolloClient, FerpClientConfig, FerpGraphQLProviderProps } from './types';

export interface FerpGraphQLContextValue {
  client: FerpApolloClient;
  config: FerpClientConfig;
}

export const FerpGraphQLContext = createContext<FerpGraphQLContextValue | null>(null);

/**
 * Production GraphQL Provider Component
 */
export const FerpGraphQLProvider: React.FC<FerpGraphQLProviderProps> = ({
  config,
  children,
  fallback: FallbackComponent,
}) => {
  const [client, setClient] = useState<FerpApolloClient | null>(null);
  const [error, setError] = useState<Error | null>(null);
  
  useEffect(() => {
    try {
      const ferpClient = createFerpClient(config);
      setClient(ferpClient);
      setError(null);
      
      // Perform initial health check
      ferpClient.healthCheck().catch(err => {
        console.warn('Initial GraphQL health check failed:', err);
      });
      
    } catch (err) {
      setError(err as Error);
    }
  }, [config]);
  
  // Error boundary fallback
  if (error) {
    if (FallbackComponent) {
      return <FallbackComponent error={error} />;
    }
    
    return (
      <div style={{
        padding: '20px',
        border: '2px solid #ff4444',
        borderRadius: '8px',
        backgroundColor: '#fff5f5',
        color: '#cc0000',
        fontFamily: 'monospace'
      }}>
        <h3>🚨 GraphQL Client Error</h3>
        <p><strong>Message:</strong> {error.message}</p>
        <details>
          <summary>Stack Trace</summary>
          <pre style={{ fontSize: '12px', overflow: 'auto' }}>
            {error.stack}
          </pre>
        </details>
        <p>
          <small>
            This error indicates a critical issue with the GraphQL client configuration.
            Please check your network connection and configuration settings.
          </small>
        </p>
      </div>
    );
  }
  
  if (!client) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '200px',
        color: '#666'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #3498db',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p>Initializing GraphQL Client...</p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }
  
  const contextValue: FerpGraphQLContextValue = {
    client,
    config,
  };
  
  return (
    <FerpGraphQLContext.Provider value={contextValue}>
      <ApolloProvider client={client}>
        {children}
      </ApolloProvider>
    </FerpGraphQLContext.Provider>
  );
};

/**
 * Development Helper Component - Shows GraphQL Client Status
 */
export const GraphQLDevTools: React.FC = () => {
  const context = useContext(FerpGraphQLContext);
  
  if (!context || context.config.environment === 'production') {
    return null;
  }
  
  const [metrics, setMetrics] = useState(context.client.getMetrics());
  const [isHealthy, setIsHealthy] = useState<boolean | null>(null);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setMetrics(context.client.getMetrics());
      context.client.healthCheck().then(setIsHealthy);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [context.client]);
  
  const healthColor = isHealthy === null ? '#ffa500' : isHealthy ? '#00aa00' : '#ff4444';
  
  return (
    <div style={{
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      background: 'rgba(0, 0, 0, 0.9)',
      color: 'white',
      padding: '12px',
      borderRadius: '8px',
      fontSize: '12px',
      fontFamily: 'monospace',
      zIndex: 10000,
      minWidth: '300px',
      maxWidth: '400px'
    }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        marginBottom: '8px',
        borderBottom: '1px solid #333',
        paddingBottom: '8px'
      }}>
        <div style={{
          width: '10px',
          height: '10px',
          borderRadius: '50%',
          backgroundColor: healthColor,
          marginRight: '8px'
        }} />
        <strong>GraphQL Client Status</strong>
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
        <div>
          <div>Requests: {metrics.totalRequests}</div>
          <div>Success: {metrics.successfulRequests}</div>
          <div>Failed: {metrics.failedRequests}</div>
        </div>
        <div>
          <div>Avg Time: {Math.round(metrics.averageResponseTime)}ms</div>
          <div>Cache Rate: {Math.round(metrics.cacheHitRate * 100)}%</div>
          <div>Retry Rate: {Math.round(metrics.retryRate * 100)}%</div>
        </div>
      </div>
      
      {metrics.failedRequests > 0 && (
        <details style={{ marginTop: '8px' }}>
          <summary style={{ cursor: 'pointer', color: '#ff8888' }}>
            Error Breakdown
          </summary>
          <div style={{ marginTop: '4px', fontSize: '11px' }}>
            {Object.entries(metrics.errorsByType).map(([code, count]) => (
              <div key={code}>
                {code}: {count}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

// Alias for backwards compatibility
export { FerpGraphQLProvider as EnhancedGraphQLProvider };
